import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Divider,
  Grid,
  Header,
  Segment,
  Table,
  Accordion,
  Icon
} from 'semantic-ui-react';
import { servicesAPI, useCustomers } from '../../_services';
import { dbUTCDatetimeToLocal, getLocalAuthorityInfo } from '../../utils';
import { history } from '../../_helpers';

const formatCurrency = (currency, amountPounds) => {
  if (isNaN(amountPounds)) return '?';
  const absAmountPounds = Math.abs(amountPounds);
  return (amountPounds < 0 ? '-' : '') + currency + absAmountPounds.toFixed(2);
};

export const ListBills = () => {
  const allCustomers = useCustomers();

  const [billingTimestamp, setBillingTimestamp] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [unbilledCustomers, setUnbilledCustomers] = useState([]);
  const [activeCustomerId, setActiveCustomerId] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [unbilledCustomersTotal, setUnbilledCustomersTotal] = useState(0);

  useEffect(() => {
    if (!allCustomers.length) return;

    // Get unbilled sessionlets
    servicesAPI.MeteringServices('getUnbilledSessionlets').then(billing => {
      setBillingTimestamp(dbUTCDatetimeToLocal(billing.billing_timestamp));
      let sessionlets = billing.sessionlets;

      // Get billing summary
      servicesAPI.MeteringServices('getBillingSummary').then(summaries => {
        const values = {
          customer_to_lance_ids: sessionlets.map(sl => sl.lance_customer_id)
        };

        // Get customer names
        servicesAPI
          .CustomerServices('getCustomerNamesFromCustomerToLances', values)
          .then(customerToLances => {
            // Get customers
            sessionlets.forEach(sessionlet => {
              const customerToLance = customerToLances.find(
                cl => cl.customer_to_lance_id === sessionlet.lance_customer_id
              );
              sessionlet.customer_id = customerToLance?.customer_id;
            });
            sessionlets = _.orderBy(sessionlets, 'customer_id', 'asc');

            let customers = [];
            let currCustomerId = -1;
            sessionlets.forEach(sessionlet => {
              if (sessionlet.customer_id !== currCustomerId) {
                currCustomerId = sessionlet.customer_id;

                const customer = allCustomers.find(
                  c => c.id === currCustomerId
                );

                // Billing summary
                let summary = summaries.find(
                  s => s.customer_id === currCustomerId
                );
                if (!summary) {
                  summary = {
                    max_session_consumption_to_date: '?',
                    total_consumption_kwh_readings: '?',
                    total_consumption_kwh_session: '?',
                    outstanding_balance_pnds: '?',
                    total_cost_pnds: '?',
                    charging_credit_used_to_date: '?'
                  };
                }

                customers.push({
                  id: currCustomerId,
                  local_authority: getLocalAuthorityInfo(customer).name,
                  forename: customer?.forename,
                  surname: customer?.surname,
                  sessions: [],
                  total_consumption: 0,
                  total_cost: 0,
                  ...summary
                });
              }
            });

            customers = _.sortBy(customers, ['local_authority', 'surname']);

            customers.forEach(customer => {
              let customerSessionlets = sessionlets.filter(
                sessionlet => sessionlet.customer_id === customer.id
              );
              customerSessionlets = _.orderBy(
                customerSessionlets,
                'session_start_datetime',
                'desc'
              );

              let currSessionId = -1;
              customerSessionlets.forEach(sessionlet => {
                customer.total_consumption += sessionlet.sl_consumption / 1000;
                customer.total_cost += sessionlet.session_cost;

                if (sessionlet.session_id !== currSessionId) {
                  currSessionId = sessionlet.session_id;

                  if (
                    sessionlet.charging_location &&
                    sessionlet.from_value &&
                    isNaN(parseInt(sessionlet.charging_location.slice(-2)))
                  ) {
                    sessionlet.charging_location =
                      sessionlet.charging_location +
                      ' CS' +
                      sessionlet.from_value.slice(-2);
                  } else {
                    sessionlet.charging_location =
                      sessionlet.charging_location.slice(0, -2) +
                      ' CS' +
                      sessionlet.charging_location.slice(-2);
                  }

                  customer.sessions.push({
                    id: currSessionId,
                    start_datetime: sessionlet.session_start_datetime,
                    stop_datetime: sessionlet.session_stop_datetime,
                    charging_location: sessionlet.charging_location,
                    total_consumption: 0,
                    total_cost: 0
                  });
                }
              });

              customer.sessions.forEach(session => {
                let sessionSessionlets = customerSessionlets.filter(
                  sessionlet => sessionlet.session_id === session.id
                );
                sessionSessionlets = _.orderBy(
                  sessionSessionlets,
                  'start_datetime',
                  'desc'
                );

                sessionSessionlets.forEach(sessionlet => {
                  session.total_consumption += sessionlet.sl_consumption / 1000;
                  session.total_cost += sessionlet.session_cost;
                });

                session.sessionlets = sessionSessionlets;
              });
            });

            setCustomers(customers);
          });
      });
    });
  }, [allCustomers]); // eslint-disable-line

  useEffect(() => {
    if (!customers.length) return;

    servicesAPI.BillingServices('getAccounts').then(accounts => {
      const unbilledAccounts = accounts.filter(
        account =>
          !customers.map(c => c.id).includes(account.customer_id) &&
          account.balance_whpence > 0
      );

      const values = {
        customer_ids: unbilledAccounts.map(a => a.customer_id)
      };

      servicesAPI
        .CustomerServices('getCustomerNamesFromCustomerIds', values)
        .then(customerNames => {
          const unbilledCustomers = unbilledAccounts.map(account => {
            const customer = customerNames.find(
              c => c.customer_id === account.customer_id
            );

            const name = customer
              ? customer.forename + ' ' + customer.surname
              : '\\_(ツ)_/';

            return {
              ...account,
              customer_name: name
            };
          });
          setUnbilledCustomers(unbilledCustomers);

          let customersTotal = 0;
          customers.forEach(c => {
            const balance = parseFloat(c.outstanding_balance_pnds);
            if (balance > 0) customersTotal += balance;
          });
          setCustomersTotal(customersTotal);

          let unbilledCustomersTotal = 0;
          unbilledCustomers.forEach(
            c => (unbilledCustomersTotal += c.balance_whpence)
          );
          setUnbilledCustomersTotal(unbilledCustomersTotal / 100000);
        });
    });
  }, [customers]); // eslint-disable-line

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>
            <Header as="h2">Unbilled Charging Sessions</Header>
            Last billing run: {billingTimestamp}
            <br />
            Billing Total: {formatCurrency('£', customersTotal)} +{' '}
            {formatCurrency('£', unbilledCustomersTotal)} ={' '}
            {formatCurrency('£', customersTotal + unbilledCustomersTotal)}
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Grid>
        <Table celled fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                Local
                <br />
                Authority
              </Table.HeaderCell>
              <Table.HeaderCell>
                Customer
                <br />
                Name
              </Table.HeaderCell>
              <Table.HeaderCell>
                Max. Consumption
                <br />
                (kWh) To Date
              </Table.HeaderCell>
              <Table.HeaderCell>
                Total Consumption
                <br />
                (kWh) Readings
              </Table.HeaderCell>
              <Table.HeaderCell>
                Total Consumption
                <br />
                (kWh) Session
              </Table.HeaderCell>
              <Table.HeaderCell>
                Outstanding
                <br />
                Balance (£)
              </Table.HeaderCell>
              <Table.HeaderCell>
                Total
                <br />
                Cost (£)
              </Table.HeaderCell>
              <Table.HeaderCell>
                Charging Credit (£)
                <br />
                Used To Date
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {customers.map(customer => (
              <>
                <Table.Row
                  key={customer.id}
                  onClick={() => {
                    if (customer.id === activeCustomerId)
                      setActiveCustomerId(null);
                    else setActiveCustomerId(customer.id);
                  }}
                >
                  <Table.Cell>{customer.local_authority}</Table.Cell>
                  <Table.Cell>
                    {customer.forename} {customer.surname}
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'right' }}>
                    {customer.max_session_consumption_to_date}
                  </Table.Cell>
                  {customer.total_consumption_kwh_readings ===
                  customer.total_consumption_kwh_session ? (
                    <>
                      <Table.Cell style={{ textAlign: 'right' }}>
                        {customer.total_consumption_kwh_readings}
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'right' }}>
                        {customer.total_consumption_kwh_session}
                      </Table.Cell>
                    </>
                  ) : (
                    <>
                      <Table.Cell error style={{ textAlign: 'right' }}>
                        {customer.total_consumption_kwh_readings}
                      </Table.Cell>
                      <Table.Cell error style={{ textAlign: 'right' }}>
                        {customer.total_consumption_kwh_session}
                      </Table.Cell>
                    </>
                  )}
                  {customer.outstanding_balance_pnds ===
                  customer.total_cost_pnds ? (
                    <>
                      <Table.Cell style={{ textAlign: 'right' }}>
                        {formatCurrency('£', customer.outstanding_balance_pnds)}
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'right' }}>
                        {formatCurrency('£', customer.total_cost_pnds)}
                      </Table.Cell>
                    </>
                  ) : (
                    <>
                      <Table.Cell error style={{ textAlign: 'right' }}>
                        {formatCurrency('£', customer.outstanding_balance_pnds)}
                      </Table.Cell>
                      <Table.Cell error style={{ textAlign: 'right' }}>
                        {formatCurrency('£', customer.total_cost_pnds)}
                      </Table.Cell>
                    </>
                  )}
                  <Table.Cell style={{ textAlign: 'right' }}>
                    {formatCurrency('£', customer.charging_credit_used_to_date)}
                  </Table.Cell>
                </Table.Row>
                {customer.id === activeCustomerId && (
                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Session ID</Table.HeaderCell>
                        <Table.HeaderCell>Start</Table.HeaderCell>
                        <Table.HeaderCell>End</Table.HeaderCell>
                        <Table.HeaderCell>Location</Table.HeaderCell>
                        <Table.HeaderCell>Consumption (kWh)</Table.HeaderCell>
                        <Table.HeaderCell>Cost</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {customer.sessions &&
                        customer.sessions.map(session => (
                          <>
                            <Table.Row>
                              <Table.Cell>{session.id}</Table.Cell>
                              <Table.Cell>
                                {dbUTCDatetimeToLocal(session.start_datetime)}
                              </Table.Cell>
                              <Table.Cell>
                                {dbUTCDatetimeToLocal(session.stop_datetime)}
                              </Table.Cell>
                              <Table.Cell>
                                {session.charging_location}
                              </Table.Cell>
                              <Table.Cell>
                                {session.total_consumption}
                              </Table.Cell>
                              <Table.Cell>
                                {formatCurrency(
                                  '£',
                                  session.total_cost / 100000
                                )}
                              </Table.Cell>
                            </Table.Row>
                            <Accordion>
                              <Accordion.Title
                                active={session.id === activeSessionId}
                                index={0}
                                onClick={() => {
                                  if (session.id === activeSessionId)
                                    setActiveSessionId(null);
                                  else setActiveSessionId(session.id);
                                }}
                              >
                                <Header as="h4">
                                  <Icon name="dropdown" />
                                  Sessionlets
                                </Header>
                              </Accordion.Title>
                              <Accordion.Content
                                active={session.id === activeSessionId}
                              >
                                <Table celled>
                                  <Table.Header>
                                    <Table.Row>
                                      <Table.HeaderCell>
                                        Sessionlet Start
                                      </Table.HeaderCell>
                                      <Table.HeaderCell>
                                        Sessionlet End
                                      </Table.HeaderCell>
                                      <Table.HeaderCell>
                                        Consumption (Wh)
                                      </Table.HeaderCell>
                                      <Table.HeaderCell>Cost</Table.HeaderCell>
                                      <Table.HeaderCell>
                                        Tariff
                                      </Table.HeaderCell>
                                      <Table.HeaderCell>
                                        Same Meters
                                      </Table.HeaderCell>
                                    </Table.Row>
                                  </Table.Header>
                                  <Table.Body>
                                    {session.sessionlets &&
                                      session.sessionlets.map(sessionlet => (
                                        <Table.Row>
                                          <Table.Cell>
                                            {dbUTCDatetimeToLocal(
                                              sessionlet.start_datetime
                                            )}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {dbUTCDatetimeToLocal(
                                              sessionlet.stop_datetime
                                            )}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {sessionlet.sl_consumption}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {sessionlet.tariff}p
                                          </Table.Cell>
                                          <Table.Cell>
                                            {formatCurrency(
                                              '£',
                                              sessionlet.session_cost / 100000
                                            )}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {sessionlet.same_meters}
                                          </Table.Cell>
                                        </Table.Row>
                                      ))}
                                  </Table.Body>
                                </Table>
                              </Accordion.Content>
                            </Accordion>
                          </>
                        ))}
                    </Table.Body>
                  </Table>
                )}
              </>
            ))}
          </Table.Body>
        </Table>
      </Grid>
      <Divider hidden />
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>
            <Header as="h2">Customers With Outstanding Balance</Header>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Grid>
        <Table celled fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Customer Name</Table.HeaderCell>
              <Table.HeaderCell>Balance (£)</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {unbilledCustomers.map(account => (
              <Table.Row
                key={account.customer_id}
                onClick={() => {
                  history.push(`/admin/customer/${account.customer_id}`);
                }}
              >
                <Table.Cell>{account.customer_name}</Table.Cell>
                <Table.Cell style={{ textAlign: 'right' }}>
                  {formatCurrency('£', account.balance_whpence / 100000)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Grid>
    </Segment>
  );
};

// Billing Screen split by Customer - shows Overall Total for all Customers which can be used to check Stripe Transaction Total
// for the Day
//
// Header row for each Customer with following columns
//   Customer
//   Max Session Consumption for a single session from their previous sessions
//   Total Session Consumption (for all sessions that have not been billed)
//   Total Reading Consumption (for all sessions that have not been billed)
//   Total Cost for Session based on Readings (for all sessions that have not been billed)
//   Outstanding Balance	(from account.account)
//   Total Cost to Date if Barnet and Balance < 0 (sum of all previous sessions)
//   HIGHLIGHT if difference between Total Session Consumption vs Total Reading Consumption
//   HIGHLIGHT if difference between Total Cost for Session vs Balance
//
// Expand Customer and see outstanding sessions to be billed
//   Session ID
// *   Location & CS	- Highlight if this is in a different Local Auhtority to their customer type
//   Session Start
//   Session End
//   Session Consumption
//
// Expand Session and see SessionLet and Reading Consumption for all Customers
//   Session Let ID
//   FromDateTime
//   To DateTime
//   Tariff
//   Consumption for Period	- Highlight if negative
//   Same Meter for Session - Y if same meter for whole session, N if not
